<template>
    <div ref="cookie-consent-list" class="qa-snoop-cookie-list">
        <snoop-button
            theme="link"
            class="snoop-modal__close-icon"
            :aria-label="translations.pages.settings.closeButton"
            @click="$emit('close', $event)"
        >
            <snoop-icon name="close" hidden />
        </snoop-button>
        <snoop-html-content tag="h2" class="snoop-modal__title" :html="translations.pages.settings.headline" />
        <snoop-html-content tag="p" class="snoop-modal__introduction" :html="translations.pages.settings.intro" />

        <snoop-collapsible
            v-for="cookie in optionalCookies"
            :id="cookie.id"
            :key="cookie.id"
            :value="acceptedCookieIds.includes(cookie.id)"
            :headline="translations.cookies[cookie.id].title"
            :requires="cookie.requires"
            :accepted-cookie-ids="acceptedCookieIds"
            has-checkbox
            @input="updateSelection(cookie.id, $event)"
        >
            <snoop-cookie-list-item :cookie-id="cookie.id" :translations="translations" :requires="cookie.requires" />
        </snoop-collapsible>

        <snoop-collapsible
            v-if="requiredCookies && requiredCookies.length"
            id="required-cookies"
            :headline="translations.pages.settings.technicallyRequired.headline"
            :subheadline="translations.pages.settings.technicallyRequired.subheadline"
        >
            <snoop-html-content tag="p" :html="translations.pages.settings.technicallyRequired.intro" />
            <div v-for="cookie in requiredCookies" :key="cookie.id">
                <snoop-cookie-list-item :cookie-id="cookie.id" :translations="translations" />
            </div>
        </snoop-collapsible>

        <div class="snoop-footer__buttons row">
            <div class="col-12 col-md-6">
                <div class="snoop-footer__link-buttons">
                    <snoop-button
                        class="qa-cookie-consent-accept-selected"
                        theme="link"
                        @click="$emit('acceptSelected')"
                    ><snoop-html-content tag="span" :html="translations.pages.settings.acceptSelected" /></snoop-button>
                    <snoop-button
                        class="qa-cookie-consent-accept-required"
                        theme="link"
                        @click="$emit('acceptTechnicallyRequired')"
                    >{{ translations.pages.settings.acceptTechnicallyRequired }}</snoop-button>
                </div>
            </div>
            <div class="col-12 col-md-6 snoop-footer__accept-all">
                <snoop-button
                    class="qa-cookie-consent-accept-all "
                    theme="primary"
                    @click="$emit('acceptAll')"
                >{{ translations.pages.settings.acceptAll }}</snoop-button>
            </div>
        </div>
    </div>
</template>

<script>
import { filter, isNil, propEq } from 'ramda'
import { hasCookieUnacceptedRequirement } from '../util/cookie'
import SnoopButton from './Button.vue'
import SnoopCollapsible from './Collapsible.vue'
import SnoopCookieListItem from './CookieListItem.vue'
import SnoopHtmlContent from './HtmlContent.vue'
import SnoopIcon from './Icon.vue'


export default {
    components: { SnoopButton, SnoopCollapsible, SnoopCookieListItem, SnoopHtmlContent, SnoopIcon },

    props: {
        translations: { type: Object, required: true },
        cookieConfig: { type: Object, required: true },
        acceptedCookieIds: { type: Array, required: true },
        scrollTo: { type: String, default: '' },
    },

    computed: {
        optionalCookies () {
            return filter(propEq('technicallyRequired', false), this.cookieConfig.cookies)
        },

        requiredCookies () {
            return filter(propEq('technicallyRequired', true), this.cookieConfig.cookies)
        },
    },

    mounted () {
        if (this.scrollTo) {
            const scrollTo = document.getElementById(`js-snoop-cookie-${this.scrollTo}`)
            scrollTo.focus()

            if (!isNil(scrollTo)) {
                scrollTo.scrollIntoView({ behavior: 'smooth' })
            }
        }
    },

    methods: {
        updateSelection (cookieId, isChecked) {
            this[isChecked ? 'addItem' : 'removeItem'](cookieId)

            // Remove items with dependencies
            this.cookieConfig.cookies.forEach(({ requires = [], id }) => {
                if (hasCookieUnacceptedRequirement(requires, this.acceptedCookieIds)) {
                    this.removeItem(id)
                }
            })
        },

        addItem (cookieId) {
            if (!this.acceptedCookieIds.includes(cookieId)) {
                this.acceptedCookieIds.push(cookieId)
            }
        },

        removeItem (cookieId) {
            const index = this.acceptedCookieIds.findIndex(x => x === cookieId)
            if (index !== -1) this.acceptedCookieIds.splice(index, 1)
        },
    },
}
</script>
